import { db, functions } from '../firebase/firebaseConfig.jsx';
import { collection, addDoc, getDocs, updateDoc, deleteDoc, doc, query, where, orderBy, serverTimestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { myanmarMessageTemplates, messageCategories, targetingOptions } from '../data/myanmarMessageTemplates';
import { messageTemplatesKo } from '../data/messageTemplatesKo';

class AdminInAppMessagingService {
  constructor() {
    this.collectionName = 'artifacts/kairos-platform-1ffde/in_app_messages';
  }

  /**
   * 메시지 템플릿 목록 조회
   */
  getMessageTemplates() {
    return myanmarMessageTemplates;
  }

  /**
   * 메시지 카테고리 목록 조회
   */
  getMessageCategories() {
    return messageCategories;
  }

  /**
   * 타겟팅 옵션 목록 조회
   */
  getTargetingOptions() {
    return targetingOptions;
  }

  /**
   * 새 메시지 생성
   */
  async createMessage(messageData) {
    try {
      // id 필드를 제거하여 Firestore가 자동으로 생성하도록 함
      const { id, ...dataWithoutId } = messageData;
      
      const message = {
        ...dataWithoutId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        status: 'draft', // draft, active, paused, completed
        sentCount: 0,
        clickCount: 0,
        createdBy: 'admin', // 실제로는 현재 admin 사용자 ID
        language: 'my' // 미얀마어로 고정
      };

      const docRef = await addDoc(collection(db, this.collectionName), message);
      console.log('✅ In-App Message created:', docRef.id);
      return { success: true, id: docRef.id };
    } catch (error) {
      console.error('❌ Failed to create In-App Message:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * 템플릿에서 메시지 생성
   */
  async createMessageFromTemplate(templateId, customizations = {}) {
    try {
      // 템플릿 찾기
      const template = this.findTemplateById(templateId);
      if (!template) {
        throw new Error('Template not found');
      }

      const ko = messageTemplatesKo[templateId];
      const messageData = {
        ...template,
        ...(ko ? { titleKo: ko.title, bodyKo: ko.body, actionKo: ko.action } : {}),
        ...customizations,
        templateId: templateId,
        isFromTemplate: true
      };

      return await this.createMessage(messageData);
    } catch (error) {
      console.error('❌ Failed to create message from template:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * 템플릿 ID로 템플릿 찾기
   */
  findTemplateById(templateId) {
    for (const category in myanmarMessageTemplates) {
      for (const templateKey in myanmarMessageTemplates[category]) {
        const template = myanmarMessageTemplates[category][templateKey];
        if (template.id === templateId) {
          return template;
        }
      }
    }
    return null;
  }

  /**
   * 메시지 목록 조회
   */
  async getMessages(filters = {}) {
    try {
      let q = collection(db, this.collectionName);
      
      // 필터 적용
      if (filters.status) {
        q = query(q, where('status', '==', filters.status));
      }
      if (filters.category) {
        q = query(q, where('category', '==', filters.category));
      }
      if (filters.priority) {
        q = query(q, where('priority', '==', filters.priority));
      }

      // 정렬
      q = query(q, orderBy('createdAt', 'desc'));

      const snapshot = await getDocs(q);
      const messages = snapshot.docs.map(doc => {
        const data = doc.data();
        console.log('📄 Message document:', {
          docId: doc.id,
          templateId: data.templateId,
          title: data.title,
          status: data.status,
          hasIdField: 'id' in data,
          idFieldValue: data.id
        });
        return {
          id: doc.id, // 항상 실제 Firestore 문서 ID 사용
          ...data,
          // id 필드가 있으면 제거 (템플릿 ID와 혼동 방지)
          id: doc.id // 실제 Firestore 문서 ID로 덮어쓰기
        };
      });

      console.log('📋 Total messages loaded:', messages.length);
      return { success: true, data: messages };
    } catch (error) {
      console.error('❌ Failed to get messages:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * 메시지 업데이트
   */
  async updateMessage(messageId, updateData) {
    try {
      const messageRef = doc(db, this.collectionName, messageId);
      await updateDoc(messageRef, {
        ...updateData,
        updatedAt: serverTimestamp()
      });

      console.log('✅ Message updated:', messageId);
      return { success: true };
    } catch (error) {
      console.error('❌ Failed to update message:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * 메시지 삭제
   */
  async deleteMessage(messageId) {
    try {
      console.log('🗑️ Deleting message with ID:', messageId);
      console.log('📁 Collection path:', this.collectionName);
      
      if (!messageId) {
        throw new Error('Message ID is required for deletion');
      }
      
      await deleteDoc(doc(db, this.collectionName, messageId));
      console.log('✅ Message deleted:', messageId);
      return { success: true };
    } catch (error) {
      console.error('❌ Failed to delete message:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * 메시지 발송 (상태를 active로 변경)
   */
  async sendMessage(messageId) {
    try {
      console.log('🚀 Sending message with ID:', messageId);
      console.log('📁 Collection path:', this.collectionName);
      
      // 먼저 Firebase Functions 호출 (draft 상태에서)
      const functionResult = await this.triggerMessageSend(messageId);
      
      if (functionResult.success) {
        // Functions 성공 후 상태를 active로 변경
        const result = await this.updateMessage(messageId, { 
          status: 'active',
          sentAt: serverTimestamp()
        });
        return result;
      } else {
        return functionResult;
      }
    } catch (error) {
      console.error('❌ Failed to send message:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * 메시지 일시정지
   */
  async pauseMessage(messageId) {
    return await this.updateMessage(messageId, { status: 'paused' });
  }

  /**
   * 메시지 재개
   */
  async resumeMessage(messageId) {
    return await this.updateMessage(messageId, { status: 'active' });
  }

  /**
   * 메시지 발송 트리거 (Firebase Functions 호출)
   */
  async triggerMessageSend(messageId) {
    try {
      console.log('🚀 Triggering message send:', messageId);
      
      // Firebase Functions 호출
      const sendInAppMessage = httpsCallable(functions, 'sendInAppMessage');
      
      // Firebase Functions 호출
      const result = await sendInAppMessage({ messageId });
      console.log('✅ Message send triggered for:', messageId, result.data);
      
      return { success: true, data: result.data };
    } catch (error) {
      console.error('❌ Failed to trigger message send:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * 사용자 타겟팅 정보 조회
   */
  async getTargetingInfo(targetUsers) {
    try {
      // 실제 구현에서는 사용자 데이터베이스에서 조회
      const mockTargetingInfo = {
        all_users: { count: 1250, description: '전체 사용자' },
        myanmar_workers: { count: 1200, description: '미얀마 근로자' },
        seoul_users: { count: 450, description: '서울 사용자' },
        gyeonggi_users: { count: 380, description: '경기도 사용자' },
        inactive_users: { count: 200, description: '비활성 사용자' },
        new_users: { count: 50, description: '신규 사용자' }
      };

      return { 
        success: true, 
        data: mockTargetingInfo[targetUsers] || { count: 0, description: '알 수 없음' }
      };
    } catch (error) {
      console.error('❌ Failed to get targeting info:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * 메시지 통계 조회
   */
  async getMessageStats(messageId) {
    try {
      // 실제 구현에서는 Analytics 데이터 조회
      const mockStats = {
        sentCount: 1250,
        deliveredCount: 1200,
        clickCount: 45,
        conversionCount: 12,
        clickRate: 3.75,
        conversionRate: 1.0
      };

      return { success: true, data: mockStats };
    } catch (error) {
      console.error('❌ Failed to get message stats:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * 일괄 메시지 발송
   */
  async sendBulkMessages(messageIds) {
    try {
      const results = [];
      for (const messageId of messageIds) {
        const result = await this.sendMessage(messageId);
        results.push({ messageId, ...result });
      }
      
      return { success: true, results };
    } catch (error) {
      console.error('❌ Failed to send bulk messages:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * 스케줄된 메시지 생성
   */
  async createScheduledMessage(messageData, scheduleTime) {
    try {
      const message = {
        ...messageData,
        status: 'scheduled',
        scheduledAt: scheduleTime,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(collection(db, this.collectionName), message);
      return { success: true, id: docRef.id };
    } catch (error) {
      console.error('❌ Failed to create scheduled message:', error);
      return { success: false, error: error.message };
    }
  }
}

// 싱글톤 인스턴스 생성
const adminInAppMessagingService = new AdminInAppMessagingService();

export default adminInAppMessagingService;
